import { email as emailValidator } from '@vuelidate/validators';
import { computed, ref } from 'vue';

import { readableServerMessage, serverErrorCode, serverErrorDetail } from '@tallo/design-system';

import { CompanyRole } from '../enums/company-role.enum';
import {
  PendingInviteDto,
  alreadyInvitedErrorCode,
  alreadyMemberErrorCode,
  emailTakenErrorCode,
} from '../interfaces/team.dto';
import teamService from '../services/team.service';

/** One address typed into a form that invites several people at once, before it is sent */
export interface InviteDraft {
  id: number;
  email: string;
  role: CompanyRole;
  /** Validation waits for the first blur, so a half-typed address is not flagged mid-word */
  touched: boolean;
  /** The server's refusal of the address last sent from this row; it lapses once the address changes */
  refusal?: { email: string; message: string; inviteId?: string };
}

/** Refusals about the address in one row: that row says why, and the other rows still go out */
const rowRefusalCodes = [alreadyMemberErrorCode, alreadyInvitedErrorCode, emailTakenErrorCode];

const isEmail = (value: string) => emailValidator.$validator(value, undefined, undefined) === true;

/**
 * Several invites typed as rows and sent together. Each row is checked on its own, and a refusal
 * about one address (already on the team, already invited, another account's) stays on that row
 * while the rest go out.
 */
export function useInviteDrafts() {
  let nextId = 0;
  const newDraft = (email = '', touched = false): InviteDraft => ({
    id: nextId++,
    email,
    role: CompanyRole.MEMBER,
    touched,
  });

  const drafts = ref<InviteDraft[]>([newDraft()]);
  /** What this form has sent, oldest first */
  const sent = ref<PendingInviteDto[]>([]);

  const address = (draft: InviteDraft) => draft.email.trim();

  function refusalOf(draft: InviteDraft): InviteDraft['refusal'] {
    return draft.refusal && draft.refusal.email === address(draft) ? draft.refusal : undefined;
  }

  /** The same address typed into an earlier row: one invite is enough */
  function isDuplicate(draft: InviteDraft): boolean {
    const value = address(draft).toLowerCase();
    return Boolean(value) && drafts.value.find((other) => address(other).toLowerCase() === value) !== draft;
  }

  function draftError(draft: InviteDraft): string | undefined {
    const refusal = refusalOf(draft);
    if (refusal) return refusal.message;
    if (!draft.touched || !address(draft)) return undefined;
    if (!isEmail(address(draft))) return 'Enter a valid email address';
    if (isDuplicate(draft)) return 'Already added above';
    return undefined;
  }

  /** The invite a refused row can resend instead: the address is already invited */
  function pendingInviteId(draft: InviteDraft): string | undefined {
    return refusalOf(draft)?.inviteId;
  }

  const typed = computed(() => drafts.value.filter((draft) => address(draft)));
  const sendable = computed(() =>
    typed.value.filter((draft) => isEmail(address(draft)) && !isDuplicate(draft) && !refusalOf(draft)),
  );
  /** Nothing typed is left that cannot go out: half-typed, repeated or refused */
  const allSendable = computed(() => typed.value.length === sendable.value.length);

  function add(email = ''): InviteDraft {
    const draft = newDraft(email);
    drafts.value.push(draft);
    return draft;
  }

  /** The form always keeps one row to type into */
  function remove(draft: InviteDraft) {
    drafts.value = drafts.value.filter((other) => other !== draft);
    if (!drafts.value.length) drafts.value = [newDraft()];
  }

  /**
   * Text holding several addresses (a list copied from a contacts app) becomes one row each.
   * False when it holds one address or none, which the field takes as typed.
   */
  function paste(draft: InviteDraft, text: string): boolean {
    const [first, ...rest] = text.split(/[\s,;]+/).filter(Boolean);
    if (!rest.length) return false;

    Object.assign(draft, { email: first, touched: true });
    drafts.value.splice(drafts.value.indexOf(draft) + 1, 0, ...rest.map((email) => newDraft(email, true)));
    return true;
  }

  async function sendOne(draft: InviteDraft): Promise<PendingInviteDto | null> {
    const email = address(draft);

    try {
      return await teamService.invite({ email, role: draft.role });
    } catch (error) {
      const code = serverErrorCode(error);
      if (!code || !rowRefusalCodes.includes(code)) throw error;

      const inviteId = serverErrorDetail(error, 'inviteId');
      draft.refusal = {
        email,
        message: readableServerMessage(error) ?? 'This email cannot be invited',
        inviteId: code === alreadyInvitedErrorCode && typeof inviteId === 'string' ? inviteId : undefined,
      };
      return null;
    }
  }

  /**
   * Sends every sendable row at once. A sent row leaves the form for `sent`; a refused one stays,
   * saying why. Any other failure is thrown only after every request has settled, so a retry
   * never sends again a row that was still in flight.
   */
  async function sendAll(): Promise<{ sent: PendingInviteDto[]; refused: number }> {
    const batch = [...sendable.value];
    const results = await Promise.allSettled(batch.map(sendOne));

    const delivered = batch.flatMap((draft, index) => {
      const result = results[index];
      return result.status === 'fulfilled' && result.value ? [{ draft, invite: result.value }] : [];
    });
    drafts.value = drafts.value.filter((draft) => !delivered.some((entry) => entry.draft === draft));
    if (!drafts.value.length) drafts.value = [newDraft()];
    sent.value = [...sent.value, ...delivered.map((entry) => entry.invite)];

    const failure = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');
    if (failure) throw failure.reason;

    return {
      sent: delivered.map((entry) => entry.invite),
      refused: results.filter((result) => result.status === 'fulfilled' && !result.value).length,
    };
  }

  return {
    drafts,
    sent,
    typed,
    sendable,
    allSendable,
    draftError,
    pendingInviteId,
    add,
    remove,
    paste,
    sendAll,
  };
}
